import React, { useEffect, useState } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import UserPhoto from "../../assets/blankphoto.webp";
import { useSelector } from "react-redux";
import { useRef } from "react";
import {
  queryDocByField,
  queryDocByFieldSnap,
  setDocData,
  storage,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getDocDataSnap } from "../../../utils/firebase/Firebase.utils";
import AddOccupantFormUser from "./Occupants/AddOccupantFormUser";
import OccupantDetails from "./OccupantDetails";
import MobileModal from "../../Dashboard/Components/MobileModal";
import { IoCamera, IoAdd, IoHome } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

export default function UserProfile() {
  const currentUser = useSelector((state) => state.userObject);
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [userData, setUserData] = useState({});
  const [addMode, setAddMode] = useState(false);
  const [occupantsArray, setOccupantsArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOccupant, setSelectedOccupant] = useState({});
  const [viewMode, setViewMode] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      await getDocDataSnap("users", currentUser.uid, (data) => {
        setUserData(data);
      });
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchOccupants = async () => {
      const occupantsPromises = await currentUser.linkedIdentifiers.map(
        async (identifier) => {
          const occupants = await queryDocByField(
            "residents",
            "identifier",
            "==",
            identifier
          );
          return {
            identifier: identifier,
            occupants: occupants[0]?.occupantsName
              ? occupants[0]?.occupantsName?.map((item) => ({
                  ...item,
                  principalResident: occupants[0]?.id,
                }))
              : [],
          };
        }
      );
      const occupants = await Promise.all(occupantsPromises);
      setOccupantsArray(occupants);
      setLoading(false);
    };
    fetchOccupants();
  }, [currentUser.linkedIdentifiers]);

  const handleUpload = async (e) => {
    if (!e.target.files[0]) return;

    const file = e.target.files[0];
    const storageRef = ref(storage, `userPhotos/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {},
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {
          await setDocData("users", currentUser.uid, { photoUrl: downloadURL });
          setProgress(0);
        });
      }
    );
  };

  const handleViewMode = (occupant) => {
    setViewMode(true);
    setSelectedOccupant(occupant);
  };

  return (
    <UserScreenWindow label="User Profile">
      <div className="w-full max-w-7xl mx-auto px-2 py-4 space-y-4">
        {/* Profile Header Section */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-start gap-4">
            <div className="relative group">
              <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-[#363062]/20">
                <img
                  src={userData.photoUrl || UserPhoto}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={() => inputRef.current.click()}
                className="absolute bottom-0 right-0 bg-[#363062] text-white p-1.5 rounded-full hover:bg-[#363062]/90 transition-colors"
              >
                <IoCamera className="w-3.5 h-3.5" />
              </button>
              <input
                type="file"
                className="hidden"
                ref={inputRef}
                onChange={handleUpload}
                accept="image/*"
              />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-[#363062]">
                {currentUser.displayName}
              </h2>
              <div className="text-xs text-gray-500 mt-1 space-y-1">
                {currentUser.linkedIdentifiers?.map((identifier) => (
                  <div key={identifier} className="flex items-center gap-1.5">
                    <IoHome className="w-3.5 h-3.5 text-[#363062]" />
                    <span className="text-[#363062]/70">{identifier}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {progress > 0 && (
            <div className="mt-3">
              <div className="h-1.5 bg-[#363062]/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#8FCD00] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Occupants Section */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-4 gap-2">
            <h3 className="text-sm font-semibold text-[#363062]">
              Household Members
            </h3>
            <button
              onClick={() => setAddMode(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#8FCD00] text-white rounded-lg hover:bg-[#8FCD00]/90 transition-colors text-xs"
            >
              <IoAdd className="w-3.5 h-3.5" />
              Member
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-pulse text-sm text-[#363062]/70">
                Loading...
              </div>
            </div>
          ) : occupantsArray.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#363062]/70">
              No household members registered yet
            </div>
          ) : (
            <div className="space-y-4">
              {occupantsArray.map((occupant, index) => (
                <div
                  key={index}
                  className="border-b border-gray-100 last:border-0 pb-4 last:pb-0"
                >
                  <div className="text-xs font-medium text-[#363062] mb-2">
                    {occupant.identifier}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {occupant?.occupants && occupant.occupants.length > 0 ? (
                      occupant.occupants.map((occ, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleViewMode(occ)}
                          className="bg-[#363062]/5 p-3 rounded-lg cursor-pointer hover:bg-[#363062]/10 transition-colors"
                        >
                          <div className="text-sm font-medium text-[#363062]">
                            {occ.firstName} {occ.lastName}
                          </div>
                          <div className="text-xs text-[#363062]/70 mt-0.5">
                            {occ.relationship}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-[#363062]/70 py-2">
                        No household members for this identifier
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {viewMode && (
        <MobileModal>
          <OccupantDetails
            occupant={selectedOccupant}
            setViewMode={setViewMode}
          />
        </MobileModal>
      )}

      {addMode && (
        <MobileModal>
          <AddOccupantFormUser setAddMode={setAddMode} />
        </MobileModal>
      )}
    </UserScreenWindow>
  );
}
